import React, { ReactElement, useEffect, useState } from 'react';
import { StaticProvider } from '@sm/webassets/StaticContext';
import { HelmetProvider } from '@sm/webassets/Helmet';
import { LocaleCode } from '@sm/locale';
import { L10nProviderProps } from '@sm/intl';
import SurveyBasePage from '~app/components/shared/BasePage/SurveyBasePage';
import { PageLayoutProps } from './types';
import { ExpandedSlLanguageLocale } from '~helpers/slLanguageLocale';

export const getLocaleMessages = async (
  /** Smartling locals of the 56 language set  */
  languageLocale: ExpandedSlLanguageLocale,
  /** ISO 639-1 Compliant 2-letter language code (i.e. fr, en or zh) */
  languageCode: LocaleCode
): Promise<L10nProviderProps> => {
  // Note: using allsettled inlieu of all; all rejects immediately, allsettled will wait for all promise to evaluate
  const [appMessages, webassetsMessages] = await Promise.allSettled([
    // By using webpack magic comments, it will publish the locale assets below to CDN during the build process in Team City
    import(/* webpackChunkName: "i18n/[request]" */ `../../src/locales/translated/${languageLocale}`),
    import(
      /* webpackChunkName: "i18n/webassets/[request]" */ `../../node_modules/@sm/webassets/dist/locales/translated/${languageLocale}`
    ),
  ]);
  const messages = {
    ...(appMessages.status === 'fulfilled' ? appMessages.value : []),
    ...(webassetsMessages.status === 'fulfilled' ? webassetsMessages.value : []),
  };

  return { localeMessages: messages, localeCode: languageLocale, languageCode };
};

const SurveyMonkey = ({
  children,
  layout: {
    options: { translationData: _translationData, ...layoutOptions },
  },
  staticData,
}: PageLayoutProps): ReactElement => {
  const { slLanguageLocale: languageLocale, languageCode } = staticData.environment;
  const [localeMessages, setLocaleMessages] = useState<L10nProviderProps | undefined>();
  // If languageLocale is null, undefined, or 'en-US', then don't fetch locale messages and just render
  const [isFetchingLocale, setIsFetchingLocale] = useState(languageLocale != null && languageLocale !== 'en-US');

  useEffect(() => {
    const fetchLocaleMessages = async (): Promise<void> => {
      if (languageLocale != null && languageLocale !== 'en-US') {
        const messages = await getLocaleMessages(languageLocale, languageCode as LocaleCode);
        setLocaleMessages(messages);
      }
      setIsFetchingLocale(false);
    };
    void fetchLocaleMessages();
  }, [languageLocale, languageCode]);

  if (isFetchingLocale) return <></>;

  // Wait until locale messages are fetched before rendering the JSX below, because L10nProvider in SurveyBasePage
  // expects translationData to be provided during the first render.
  return (
    <HelmetProvider>
      <StaticProvider value={staticData}>
        <SurveyBasePage translationData={localeMessages} {...layoutOptions}>
          {children}
        </SurveyBasePage>
      </StaticProvider>
    </HelmetProvider>
  );
};

export default SurveyMonkey;
